import { relative } from '@/lib/format'
import { Badge, Card, CardHeader, Skeleton, cx } from './ui/primitives'
import type { ApprovalStepSnapshot, ApprovalTrigger, QuotationApprovalView } from '@/types/api'

/**
 * Who has to approve this, and why each of them is in the chain.
 *
 * Shown while the quotation is still a draft as well as after submit. A rep who
 * only learns the chain at submit discovers a Country Manager step from a
 * discount they could have trimmed by two percent.
 */
export function ChainPreviewPanel({
  view,
  loading,
}: {
  view: QuotationApprovalView | undefined
  loading: boolean
}) {
  if (loading) {
    return (
      <Card className="p-5">
        <Skeleton className="h-24" />
      </Card>
    )
  }

  if (!view) return null

  const running = view.current
  const steps = running?.steps ?? view.preview.steps
  const currentStep = running?.currentStepOrder

  return (
    <Card>
      <CardHeader
        title={running ? 'Approval in flight' : 'Who will approve this'}
        hint={
          running
            ? 'Frozen at submit. Editing the country chain now cannot change who must approve this one.'
            : view.preview.chainName ?? undefined
        }
      />

      <div className="px-5 py-4">
        {/* A chain that resolves to nobody blocks the submit rather than producing an
            unapproved quotation, so this is a blocker and not a warning. */}
        {view.preview.blocker && (
          <p className="mb-3 rounded-md bg-crit-wash px-3 py-2 text-[13px] text-crit">
            {view.preview.blocker}
          </p>
        )}

        {steps.length === 0 && !view.preview.blocker && (
          <p className="text-[13px] text-muted">No chain resolved yet.</p>
        )}

        <ResolvedSteps steps={steps} currentStepOrder={running ? currentStep : undefined} />

        {running && running.actions.length > 0 && (
          <div className="mt-4 border-t border-line-soft pt-3">
            <p className="mb-2 font-mono text-[11px] uppercase tracking-[0.12em] text-muted">History</p>
            <ul className="flex flex-col gap-2 text-[13px]">
              {running.actions.map((action, index) => (
                <li key={`${action.actedAt}-${index}`}>
                  <span className="font-medium">{action.actorName}</span>{' '}
                  <span className="text-muted">
                    {action.action === 'Approved' ? 'approved' : 'sent it back'}{' '}
                    {relative(action.actedAt)}
                  </span>
                  {action.wasOverride && <Badge tone="warning">Override</Badge>}
                  {action.comment && <p className="text-muted">“{action.comment}”</p>}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Card>
  )
}

/**
 * A resolved chain, drawn as the stops it will actually run.
 *
 * <p>
 * Shared with the chain editor's simulator on purpose. An administrator trying a chain and a
 * rep reading the one their quotation runs are looking at the same question, so they are
 * shown the same answer in the same shape - the alternative is two drawings of one chain
 * that drift, and an administrator who signs off on something the rep never sees.
 * </p>
 *
 * Without <c>currentStepOrder</c> nothing is in flight and every stop reads as a forecast.
 */
export function ResolvedSteps({
  steps,
  currentStepOrder,
}: {
  steps: ApprovalStepSnapshot[]
  currentStepOrder?: number | undefined
}) {
  return (
    <ol className="flex flex-col gap-3">
      {steps.map((step) => (
        <Step
          key={step.stepOrder}
          step={step}
          state={
            currentStepOrder === undefined
              ? 'preview'
              : step.stepOrder < currentStepOrder
                ? 'done'
                : step.stepOrder === currentStepOrder
                  ? 'current'
                  : 'waiting'
          }
        />
      ))}
    </ol>
  )
}

type StepState = 'preview' | 'done' | 'current' | 'waiting'

function Step({ step, state }: { step: ApprovalStepSnapshot; state: StepState }) {
  return (
    <li className="flex gap-3">
      <span
        aria-hidden
        className={cx(
          'mt-1 grid size-5 shrink-0 place-items-center rounded-full font-mono text-[10px]',
          state === 'done' && 'bg-pos text-white',
          state === 'current' && 'brand-gradient text-white',
          (state === 'waiting' || state === 'preview') && 'bg-sunken text-muted',
        )}
      >
        {state === 'done' ? '✓' : step.stepOrder}
      </span>

      <div className="min-w-0">
        <p className={cx('text-[13px]', state === 'current' ? 'font-medium' : '')}>
          {step.approverLabel}
          {!step.isBlocking && <span className="ml-1.5 text-[12px] text-muted">(optional)</span>}
        </p>
        <p className="text-[12px] text-muted">{step.triggers.map(explain).join(' · ')}</p>
      </div>
    </li>
  )
}

/** Why this person is in the chain. The server writes the sentence; this is the fallback. */
function explain(trigger: ApprovalTrigger): string {
  if (trigger.explanation) return trigger.explanation

  switch (trigger.kind) {
    case 'AmountBand':
      return 'the value of this quotation'
    case 'Discount':
      return 'a discount above the country threshold'
    case 'TermsOverride':
      return 'customer-amended terms'
    default:
      return 'every quotation'
  }
}
